import { useState } from 'react'
import { X, Send, ArrowLeft, AlertTriangle, Phone, Clock, Users, Check } from 'lucide-react'
import { toast } from 'sonner'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { adminRepo } from '@/lib/repositories'
import { RateLimitError } from '@/lib/repositories/supabase/SupabaseAdminRepository'
import { analytics } from '@/lib/services/analytics'
import { AnalyticsEvents } from '@/lib/services/analytics-events'

interface SendSMSSheetProps {
  open: boolean
  onClose: () => void
  onBack: () => void
}

/**
 * Parse phone numbers from comma-separated input
 * Handles various formats: +1234567890, (123) 456-7890, 123-456-7890
 */
function parsePhoneNumbers(input: string): string[] {
  return input
    .split(',')
    .map((num) => num.trim())
    .filter((num) => num.length > 0)
    .map((num) => {
      // Clean the number, keeping only digits and leading +
      const cleaned = num.replace(/[^\d+]/g, '')
      // Add + prefix if it's a 10-digit US number without country code
      if (cleaned.length === 10 && !cleaned.startsWith('+')) {
        return `+1${cleaned}`
      }
      // Add + prefix if it's 11 digits starting with 1 (US)
      if (
        cleaned.length === 11 &&
        cleaned.startsWith('1') &&
        !cleaned.startsWith('+')
      ) {
        return `+${cleaned}`
      }
      return cleaned
    })
}

export function SendSMSSheet({ open, onClose, onBack }: SendSMSSheetProps) {
  const [phoneInput, setPhoneInput] = useState('')
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [useSubscribers, setUseSubscribers] = useState(false)

  const maxLength = 160
  const messageCount = Math.ceil(message.length / maxLength) || 1
  const phoneNumbers = parsePhoneNumbers(phoneInput)

  const hasRecipients = useSubscribers || phoneNumbers.length > 0

  const handleSend = async () => {
    if (!hasRecipients) {
      toast.error('Please enter at least one phone number or enable subscriber list')
      return
    }

    if (!message.trim()) {
      toast.error('Please enter a message')
      return
    }

    try {
      setIsSending(true)

      const result = await adminRepo.sendSMSBroadcast(
        message.trim(),
        phoneNumbers,
        useSubscribers
      )

      if (result.success) {
        analytics.capture(AnalyticsEvents.ADMIN_SMS_BROADCAST_SENT, {
          recipient_count: result.recipientCount,
          message_count: result.messageCount,
          used_subscribers: useSubscribers,
        })
        toast.success('SMS sent successfully!', {
          description: `Sent to ${result.messageCount} of ${result.recipientCount} recipients`,
        })

        setPhoneInput('')
        setMessage('')
        onClose()
      } else {
        toast.error('Some messages failed to send', {
          description: `Sent ${result.messageCount} of ${result.recipientCount}`,
        })
      }
    } catch (error) {
      console.error('Failed to send SMS:', error)

      if (error instanceof RateLimitError) {
        toast.error('Rate limit exceeded', {
          description: `Please wait ${error.retryAfter} seconds before sending again. (Limit: ${error.limit}/min)`,
          icon: <Clock className="w-4 h-4" />,
        })
      } else {
        toast.error('Failed to send SMS', {
          description:
            error instanceof Error ? error.message : 'Please try again.',
        })
      }
    } finally {
      setIsSending(false)
    }
  }

  // Reset form when closing
  const handleClose = () => {
    setPhoneInput('')
    setMessage('')
    setUseSubscribers(false)
    onClose()
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent className="bg-neutral-950 border-white/[0.06] flex flex-col h-full max-h-[90vh] w-full sm:max-w-md">
        {/* Header */}
        <SheetHeader className="flex-shrink-0 flex flex-row items-center justify-between pb-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="text-neutral-400 hover:text-white hover:bg-white/[0.06] h-9 w-9 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <SheetTitle className="text-xl font-bold text-white">
                SMS Message
              </SheetTitle>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-neutral-400 hover:text-white hover:bg-white/[0.06] h-9 w-9 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </Button>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-6 px-6 space-y-6">
          {/* Send to Subscribers Toggle */}
          <button
            type="button"
            onClick={() => setUseSubscribers(!useSubscribers)}
            disabled={isSending}
            className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-colors ${
              useSubscribers
                ? 'bg-emerald-500/10 border-emerald-500/30'
                : 'bg-neutral-900/50 border-white/[0.06] hover:border-white/[0.12]'
            }`}
          >
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                useSubscribers
                  ? 'bg-emerald-500/20'
                  : 'bg-neutral-800'
              }`}
            >
              <Users className={`w-5 h-5 ${useSubscribers ? 'text-emerald-400' : 'text-neutral-400'}`} />
            </div>
            <div className="flex-1 text-left">
              <p className={`text-sm font-medium ${useSubscribers ? 'text-emerald-400' : 'text-neutral-300'}`}>
                Send to all subscribers
              </p>
              <p className="text-xs text-neutral-500">
                Include all opted-in SMS subscribers
              </p>
            </div>
            <div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                useSubscribers
                  ? 'bg-emerald-500 border-emerald-500'
                  : 'border-neutral-600 bg-transparent'
              }`}
            >
              {useSubscribers && <Check className="w-3.5 h-3.5 text-black" />}
            </div>
          </button>

          {/* Phone Numbers Input */}
          <div className="space-y-3">
            <Label
              htmlFor="phone-numbers"
              className="text-neutral-300 flex items-center gap-2"
            >
              <Phone className="w-4 h-4" />
              Additional Phone Numbers
              {useSubscribers && (
                <span className="text-xs text-neutral-500 font-normal">(optional)</span>
              )}
            </Label>
            <Input
              id="phone-numbers"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              placeholder="+1234567890, +1987654321"
              className="bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50"
              disabled={isSending}
            />
            <p className="text-xs text-neutral-500">
              Enter phone numbers separated by commas.{' '}
              {phoneNumbers.length > 0 && (
                <span className="text-emerald-400">
                  {phoneNumbers.length} number
                  {phoneNumbers.length !== 1 ? 's' : ''} detected
                </span>
              )}
            </p>
          </div>

          {/* Message Input */}
          <div className="space-y-3">
            <Label htmlFor="sms-message" className="text-neutral-300">
              SMS Message
            </Label>
            <Textarea
              id="sms-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your SMS alert message..."
              className="min-h-[120px] bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 resize-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50"
              disabled={isSending}
            />
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-500 font-mono">
                {message.length}/{maxLength} characters
              </span>
              <span className="text-neutral-400 font-medium font-mono">
                {messageCount} SMS segment{messageCount > 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Important Warning */}
          <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-500 font-semibold text-sm mb-1">
                  Important:
                </p>
                <p className="text-amber-400/90 text-sm leading-relaxed">
                  SMS will be sent to{' '}
                  {useSubscribers
                    ? `all active subscribers${phoneNumbers.length > 0 ? ` + ${phoneNumbers.length} additional number${phoneNumbers.length !== 1 ? 's' : ''}` : ''}`
                    : `${phoneNumbers.length || 'the specified'} recipient${phoneNumbers.length !== 1 ? 's' : ''}`}
                  . Standard messaging rates apply.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 p-6 border-t border-white/[0.06] space-y-3">
          <Button
            onClick={handleSend}
            disabled={!message.trim() || !hasRecipients || isSending}
            className="w-full h-12 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-black font-semibold text-base disabled:opacity-40 shadow-lg shadow-emerald-500/25"
          >
            {isSending ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Sending...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Send SMS
              </span>
            )}
          </Button>
          <Button
            onClick={handleClose}
            variant="ghost"
            disabled={isSending}
            className="w-full h-11 text-neutral-400 hover:text-white hover:bg-white/[0.06]"
          >
            Cancel
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
